import { supabase } from './supabase';

// ─── Empty default shapes (mirror mockData structures) ─────────────────────────

export const EMPTY_NET_WORTH = {
  assets: [],
  liabilities: [],
  history: [],
  estatePlanning: [],
};

export const EMPTY_BUDGET = {
  income: 0,
  categories: [],
  transactions: [],    // { id, date, amount, description, category, type, kind, source, reviewed }
  categoryRules: {},   // learned merchant rules: { 'merchant key': { category, type } }
  rolloverEnabled: {}, // { catName: bool } — per-category rollover toggle
  rollovers: {},       // { catName: number } — amounts carried in from last month
  lastSnapshotMonth: null,  // 'YYYY-MM' of last processed month
  monthlySnapshots: {},     // { 'YYYY-MM': { catName: { budgeted, rolloverIn, effectiveBudget, spent, rolloverOut } } }
  bills: [],                // { id, name, amount, dueDay, frequency, category, paidMonths: { 'YYYY-MM': true } }
  categoryGoalLinks: {},    // { catName: goalId } — links a category to a financial goal
};

export const EMPTY_GOALS = [];

export const EMPTY_RETIREMENT = {
  accounts: [],
  projectedAt65: 0,
  successRate: 0,
  tips: [],
};

export const EMPTY_CALENDAR = [];
export const EMPTY_FAMILY   = [];

export const EMPTY_USER_PROFILE = {
  name: '',
  age: 30,
  retirementAge: 65,
  lifeExpectancy: 90,
  ssEstimate: 0,
  // Extended settings
  dob: '',
  taxFilingStatus: 'single',
  annualIncome: 0,
  stateOfResidence: '',
  employmentStatus: 'employed',
  riskTolerance: 'moderate',
  // Onboarding / survey
  onboardingComplete: false,
  surveyComplete: false,
  surveyAnswers: null,
  // Notifications (read state only — content is dynamically generated)
  readNotificationIds: [],
  // Theme
  theme: 'dark',
  // AI Advisor conversation history
  chatHistory: [],
  // Recurring transactions
  recurring: [],
  // Debts
  debts: [],
  // Linked financial accounts (both Plaid-connected and manually added)
  linkedAccounts: [],
  // Plan selection (set after onboarding survey)
  plan: null,       // 'free' | null
  waitlist: false,  // true if user joined Pro waitlist
};

export const EMPTY_USER_DATA = {
  net_worth:    EMPTY_NET_WORTH,
  budget:       EMPTY_BUDGET,
  goals:        EMPTY_GOALS,
  retirement:   EMPTY_RETIREMENT,
  rsus:         null,
  credit:       null,
  calendar:     EMPTY_CALENDAR,
  family:       EMPTY_FAMILY,
  investing:    null,
  user_profile: EMPTY_USER_PROFILE,
};

// ─── Load all sections for a user ─────────────────────────────────────────────

export async function loadUserData(userId) {
  const { data, error } = await supabase
    .from('user_financial_data')
    .select('*')
    .eq('id', userId)
    .maybeSingle();   // returns null (not error) when no row exists

  if (error) {
    console.error('[userData] load error:', error.message);
    // Return empty defaults so the app doesn't crash
    return { ...EMPTY_USER_DATA, _error: error.message };
  }

  return data;  // null = first login, row not created yet
}

// ─── Create the initial empty row for a new user ───────────────────────────────
//
// Uses upsert so that repeated calls (e.g. race conditions on first login) are
// idempotent and never produce a duplicate-key error.

export async function initUserData(userId, firstName) {
  const profile = firstName
    ? { ...EMPTY_USER_PROFILE, name: firstName }
    : EMPTY_USER_PROFILE;

  const rowToInsert = { id: userId, ...EMPTY_USER_DATA, user_profile: profile };

  const { data, error } = await supabase
    .from('user_financial_data')
    .upsert(rowToInsert, { onConflict: 'id', ignoreDuplicates: true })
    .select()
    .maybeSingle();   // null if row already existed (ignoreDuplicates), object if newly created

  if (error) {
    console.error('[userData] init error:', error.message);
    // Row might already exist — try a plain select to retrieve it
    const { data: existing } = await supabase
      .from('user_financial_data')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    return existing ?? { ...EMPTY_USER_DATA, user_profile: profile };
  }

  // If null, row already existed — fetch it so we return the persisted data
  if (!data) {
    const { data: existing } = await supabase
      .from('user_financial_data')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    return existing ?? { ...EMPTY_USER_DATA, user_profile: profile };
  }

  return data;
}

// ─── Save one section (optimistic — throws on error for rollback) ──────────────
//
// Uses UPSERT so that saves work correctly even when the user row doesn't exist
// yet (e.g. initUserData failed on first login, or the table was set up after
// the account was created).
//
// ON CONFLICT (id) — only updates the single JSONB column in the payload,
// leaving all other section columns completely untouched.

export async function saveSection(userId, section, sectionData) {
  const { error } = await supabase
    .from('user_financial_data')
    .upsert(
      { id: userId, [section]: sectionData },
      { onConflict: 'id' }
    );

  if (error) {
    console.error(`[userData] saveSection(${section}) error:`, error.message);
    throw error;
  }
}

// ─── AI Chat Session CRUD ──────────────────────────────────────────────────────
//
// Requires the following table in Supabase (run once in the SQL editor):
//
//   create table if not exists public.ai_chat_history (
//     id                 uuid primary key default gen_random_uuid(),
//     user_id            uuid not null references auth.users(id) on delete cascade,
//     conversation_title text not null default 'New Conversation',
//     messages           jsonb not null default '[]'::jsonb,
//     created_at         timestamptz not null default now(),
//     updated_at         timestamptz not null default now()
//   );
//   alter table public.ai_chat_history enable row level security;
//   create policy "owner access" on public.ai_chat_history
//     for all using (auth.uid() = user_id) with check (auth.uid() = user_id);

/** Returns list of sessions (without messages) sorted newest-first. */
export async function loadChatSessions(userId) {
  const { data, error } = await supabase
    .from('ai_chat_history')
    .select('id, conversation_title, created_at, updated_at')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('[userData] loadChatSessions error:', error.message);
    return [];
  }
  return data || [];
}

/** Returns a single session including its full messages array. */
export async function loadChatSession(sessionId) {
  const { data, error } = await supabase
    .from('ai_chat_history')
    .select('*')
    .eq('id', sessionId)
    .single();

  if (error) {
    console.error('[userData] loadChatSession error:', error.message);
    return null;
  }
  return data;
}

/** Inserts a new session and returns the created row (including id). */
export async function createChatSession(userId, title, messages) {
  const { data, error } = await supabase
    .from('ai_chat_history')
    .insert({ user_id: userId, conversation_title: title, messages })
    .select()
    .single();

  if (error) {
    console.error('[userData] createChatSession error:', error.message);
    throw error;
  }
  return data;
}

/** Updates messages (and optionally title) on an existing session. */
export async function updateChatSession(sessionId, messages, title) {
  const update = { messages, updated_at: new Date().toISOString() };
  if (title !== undefined) update.conversation_title = title;

  const { error } = await supabase
    .from('ai_chat_history')
    .update(update)
    .eq('id', sessionId);

  if (error) {
    console.error('[userData] updateChatSession error:', error.message);
    throw error;
  }
}

/** Hard-deletes a session. */
export async function deleteChatSession(sessionId) {
  const { error } = await supabase
    .from('ai_chat_history')
    .delete()
    .eq('id', sessionId);

  if (error) {
    console.error('[userData] deleteChatSession error:', error.message);
    throw error;
  }
}

// ─── Mood Check-in CRUD ────────────────────────────────────────────────────────
//
// Requires the following table in Supabase (run once in the SQL editor):
//
//   create table if not exists public.mood_checkins (
//     id         uuid primary key default gen_random_uuid(),
//     user_id    uuid not null references auth.users(id) on delete cascade,
//     mood       text not null,   -- 'great' | 'good' | 'okay' | 'stressed' | 'overwhelmed'
//     note       text,
//     week_key   text not null,   -- 'YYYY-WNN' ISO week identifier
//     created_at timestamptz not null default now()
//   );
//   alter table public.mood_checkins enable row level security;
//   create policy "owner access" on public.mood_checkins
//     for all using (auth.uid() = user_id) with check (auth.uid() = user_id);

/** Returns all check-ins for a user, sorted oldest-first. */
export async function loadMoodCheckins(userId) {
  const { data, error } = await supabase
    .from('mood_checkins')
    .select('id, mood, note, week_key, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[userData] loadMoodCheckins error:', error.message);
    return [];
  }
  return data || [];
}

/** Inserts a new mood check-in row. */
export async function saveMoodCheckin(userId, { mood, note = null, weekKey }) {
  const { error } = await supabase
    .from('mood_checkins')
    .insert({ user_id: userId, mood, note, week_key: weekKey });

  if (error) {
    console.error('[userData] saveMoodCheckin error:', error.message);
    // Non-fatal: check-in history is a nice-to-have; don't block the UI
  }
}

// ─── History snapshot helper (net worth, credit score trending) ────────────────

export function appendHistorySnapshot(history = [], newValue) {
  const monthAbbr = new Date().toLocaleDateString('en-US', { month: 'short' });
  const updated = [...history];
  const last = updated[updated.length - 1];
  if (last && last.month === monthAbbr) {
    updated[updated.length - 1] = { month: monthAbbr, value: newValue };
  } else {
    updated.push({ month: monthAbbr, value: newValue });
    if (updated.length > 12) updated.shift();
  }
  return updated;
}
